import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useContext, useEffect, useState} from 'react';

import './dashboardMenu.css';

import Menu, {Item as MenuItem, SubMenu} from 'rc-menu';

import {faAngleDown} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {IfmsConfigMenuItem} from '../../../config/types';
import {faAngleUp} from "@fortawesome/free-solid-svg-icons/faAngleUp";

import ApplicationContext from '../../../ApplicationContext';

function MenuItemText({
  item,
  selectedOperation,
  currentUrl,
  useDarkMode,
}: {
  item:IfmsConfigMenuItem,
  selectedOperation: string|undefined|null,
  currentUrl: string|undefined
  useDarkMode:boolean|undefined,
}) {
  const itemIcon = useDarkMode && item.darkModeIcon ? item.darkModeIcon : item.icon;
  const [displayIcon, setDisplayIcon] = useState<React.ReactNode|null>(item.disable && item.disableIcon ? item.disableIcon : item.selectedIcon && item.isSelected && item.isSelected(selectedOperation, currentUrl ?? null) ? item.selectedIcon : itemIcon);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setDisplayIcon(item.disable && item.disableIcon ? item.disableIcon : item.selectedIcon && item.isSelected && item.isSelected(selectedOperation, currentUrl ?? null) ? item.selectedIcon : itemIcon);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemIcon]
  );

  function onMouseEnter() {
    setIsHovered(true);
    // if (item.hoverIcon) {
    //   setDisplayIcon(useDarkMode ? item.darkModeIcon : item.hoverIcon);
    // }
  }

  function onMouseLeave() {
    setIsHovered(false);
    setDisplayIcon(item.disable && item.disableIcon ? item.disableIcon : item.selectedIcon && item.isSelected && item.isSelected(selectedOperation, currentUrl ?? null) ? item.selectedIcon : itemIcon);
  }

  const containerStyle: React.CSSProperties = {
    height:'32px',
    borderBottom: isHovered ? !useDarkMode ? '3px solid #0079A4' : '3px solid #ffffff' : '',
    cursor: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="rgba(171,18,120,0.95)"/></svg>') 7.5 7.5, auto`,
  };

  const commonTextStyle: React.CSSProperties = {
    verticalAlign: 'middle',
    color: item.disable ? 'gray' : useDarkMode ? 'white' : 'black',
    lineHeight: '32px',
    fontWeight:'bold',
  };

  return (
    <div
      style={containerStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {displayIcon && <span style={{marginRight:'4px'}}>{displayIcon}</span>}
      <span
        style={commonTextStyle}>
        {item.text}
      </span>
    </div>
  );
}

function isSubmenuSelected(item:IfmsConfigMenuItem, selectedOperation:string|null|undefined, currentUrl:string) {
  return !!item.isSelected && item.isSelected(selectedOperation, currentUrl);
}

type MenuItemFactoryProps = {
  item: IfmsConfigMenuItem,
  customSubmenuArrow?: boolean,
  selectedOperation?: string|null,
  currentUrl?: string,
  customMenuClass?: string|undefined,
  customPopupClass?: string|undefined,
  topLineColor?: string,
  useDarkMode?: boolean,
};

type DashboardMenuProps = {
  onMenuItemSelected: (menuItem:IfmsConfigMenuItem) => void,
  items: Array<IfmsConfigMenuItem>,
  menuStyle?: React.CSSProperties|undefined,
  selectedOperation?: string|null,
  currentUrl?: string,
  customMenuClass?: string|undefined,
  customPopupClass?: string|undefined,
};

interface MenuItemMap {
  [indexer: string] : IfmsConfigMenuItem
};

const MenuItemFactory:React.FC<MenuItemFactoryProps> = ({ item, customMenuClass, customSubmenuArrow, selectedOperation, currentUrl, customPopupClass, topLineColor, useDarkMode, ...other }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!item.items || item.items.length <= 0) {
    if (item.level === 0) {
      return <MenuItem key={item.menuId} name={item.name} {...other} className={customMenuClass} data-submenuselected={isSubmenuSelected(item, selectedOperation, currentUrl ?? '') ? 'true' : 'false'}>
        <div className='ifms-dashboard-menu-item'>
          <MenuItemText item={item} selectedOperation={selectedOperation} currentUrl={currentUrl} useDarkMode={useDarkMode} />
        </div>
      </MenuItem>;
    } else {
      return <>
        <div className='top-hr-line' style={{borderColor: topLineColor}} />
        <MenuItem key={item.menuId} {...other}>
          <div className='ifms-dashboard-sub-menu-item'>
            <MenuItemText item={item} selectedOperation={selectedOperation} currentUrl={currentUrl} useDarkMode={useDarkMode} />
          </div>
        </MenuItem>
      </>;
    }
  }

  const carretIcon = item.hideCaret ? null : <FontAwesomeIcon style={{verticalAlign: 'middle'}} icon={isHovered ? faAngleUp : faAngleDown} />;

  const submenuContent = customSubmenuArrow
    ? <div className='ifms-dashboard-menu-item'>
        <MenuItemText item={item} selectedOperation={selectedOperation} currentUrl={currentUrl} useDarkMode={useDarkMode} />&nbsp;{carretIcon}
      </div>
    : <div className=''>
      <MenuItemText item={item} selectedOperation={selectedOperation} currentUrl={currentUrl} useDarkMode={useDarkMode} />&nbsp;{carretIcon}
    </div>;
  
  return (
    <SubMenu onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} key={item.menuId} popupClassName={`${customPopupClass || ''} ifms-dashboard-menu`} className={customMenuClass} title={submenuContent} {...other} data-submenuselected={isSubmenuSelected(item, selectedOperation, currentUrl ?? '') ? 'true' : 'false'}>
      {item.items.map(si => (
        <MenuItemFactory item={si} key={si.menuId} topLineColor={topLineColor} />
      ))}
    </SubMenu>
  );
}

function withMenuId(items:Array<IfmsConfigMenuItem>, menuItemMap:MenuItemMap, selectedKeys:Array<string>, selectedOperation:string, currentUrl:string):Array<IfmsConfigMenuItem> {
  return items.map(x => {
    const menuId = x.customId || _.uniqueId();
    if (!!x.isSelected && x.isSelected(selectedOperation, currentUrl)) {
      selectedKeys.push(menuId);
    }

    const result:IfmsConfigMenuItem = {
      menuId,
      ...x,
      items: x.items ? withMenuId(x.items, menuItemMap, selectedKeys, selectedOperation, currentUrl) : [],
    };

    menuItemMap[menuId] = result;

    return result;
  });
}

const DashboardMenu: React.FC<DashboardMenuProps> = ({ items, onMenuItemSelected, menuStyle, selectedOperation, currentUrl, customMenuClass, customPopupClass, ...other }) => {
  const appContext = useContext(ApplicationContext);
  const useDarkMode = appContext.useDarkMode;

  let tempCustomMenuClass = customMenuClass ? `${customMenuClass} ` : '';
  tempCustomMenuClass += useDarkMode ? 'ifms-dashboard-menu-darkmode' : 'ifms-dashboard-menu';

  const menuItemMap:MenuItemMap = {};

  const selectedKeys:Array<string>=[];
  const menuItems = withMenuId(items, menuItemMap, selectedKeys, selectedOperation ?? '', currentUrl ?? '');

  function procesMenuSelection(e:any) {
    const menuKey:string = e.key as string;

    if (menuItemMap[menuKey]) {
      onMenuItemSelected(menuItemMap[menuKey]);
    }
  }

  return (
    <Menu className="ifms-dashboard-menu" multiple mode="horizontal" onClick={procesMenuSelection} style={menuStyle} selectedKeys={selectedKeys} {...other}>
      {menuItems.map(item => {
        return (
          <MenuItemFactory topLineColor={item.topLineColor} customPopupClass={customPopupClass} customMenuClass={tempCustomMenuClass} item={item} key={item.menuId} customSubmenuArrow selectedOperation={selectedOperation} currentUrl={currentUrl} useDarkMode={useDarkMode} />
        )
      })}
    </Menu>
  );
};

DashboardMenu.defaultProps = {
  onMenuItemSelected: () => {},
  items: [],
};

DashboardMenu.propTypes = {
  onMenuItemSelected: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  menuStyle: PropTypes.object,
  customMenuClass: PropTypes.string,
};


export default React.memo(DashboardMenu);